import React, { useState, useEffect } from 'react';
import { Plus, Users, Target, Download, Copy, Trash2, Edit, Play, Pause, Cloud } from 'lucide-react';
import segmentService from '../../services/segmentService';

interface Segment {
  id: number;
  name: string;
  description: string;
  inclusion_rules: any;
  exclusion_rules: any;
  priority: number;
  is_active: boolean;
  email_list_ready: boolean;
  never_contact: boolean;
  auto_tag: boolean;
  active_leads: number;
  total_leads: number;
  conversion_rate?: number;
  last_evaluated?: string;
  created_at: string;
}

interface SegmentDashboardProps {
  onCreateSegment: () => void;
  onEditSegment: (segment: Segment) => void;
  onViewSegment: (segment: Segment) => void;
  refreshTrigger?: number; // Add refresh trigger prop
}

const SegmentDashboard: React.FC<SegmentDashboardProps> = ({
  onCreateSegment,
  onEditSegment,
  onViewSegment,
  refreshTrigger
}) => {
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive' | 'email_ready'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchSegments();
  }, [refreshTrigger]); // Refresh when refreshTrigger changes

  const fetchSegments = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔄 SegmentDashboard: Fetching segments...');
      
      const data = await segmentService.getSegments();
      console.log('📊 SegmentDashboard: Received segments:', data);
      
      setSegments(data);
    } catch (err) {
      console.error('❌ SegmentDashboard: Error fetching segments:', err);
      setError('Failed to fetch segments');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (segmentId: number, isActive: boolean) => {
    try {
      await segmentService.updateSegment(segmentId, { is_active: !isActive });
      fetchSegments(); // Refresh list
    } catch (err) {
      console.error('Error toggling segment:', err);
      setError('Failed to toggle segment status');
    }
  };

  const handleCloneSegment = async (segmentId: number, name: string) => {
    try {
      const newName = window.prompt('Enter name for cloned segment:', `${name} (Copy)`);
      if (!newName) return;

      await segmentService.cloneSegment(segmentId, newName);
      fetchSegments(); // Refresh list
    } catch (err) {
      console.error('Error cloning segment:', err);
      setError('Failed to clone segment');
    }
  };

  const handleDeleteSegment = async (segmentId: number, name: string) => {
    if (!window.confirm(`Are you sure you want to delete segment "${name}"?`)) return;

    try {
      await segmentService.deleteSegment(segmentId);
      fetchSegments(); // Refresh list
    } catch (err) {
      console.error('Error deleting segment:', err);
      setError('Failed to delete segment');
    }
  };

  const handleExportSegment = async (segmentId: number, name: string) => {
    try {
      const blob = await segmentService.exportSegment(segmentId, 'csv');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name.replace(/[^a-zA-Z0-9]/g, '_')}_leads.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting segment:', err);
      setError('Failed to export segment');
    }
  };

  const handleSyncToMailerLite = async (segmentId: number, name: string) => {
    if (!window.confirm(`Sync segment "${name}" to MailerLite? This will create a MailerLite group and upload all leads from this segment.`)) {
      return;
    }

    try {
      setError(null);
      const result = await segmentService.syncToMailerLite(segmentId);
      
      if (result.success) {
        const { results } = result;
        let message = `✅ Sync completed!\n\n`;
        message += `MailerLite Group: ${result.mailerlite_group_name}\n`;
        message += `Leads Found: ${results.leads_found}\n`;
        message += `Successfully Synced: ${results.synced}\n`;
        
        if (results.errors > 0) {
          message += `Errors: ${results.errors}\n\n`;
          message += `Check console for error details.`;
        }
        
        alert(message);
        console.log('Sync results:', result);
      }
    } catch (err) {
      console.error('Error syncing to MailerLite:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to sync to MailerLite';
      setError(errorMessage);
      alert(`❌ Sync failed: ${errorMessage}`);
    }
  };

  const filteredSegments = segments.filter(segment => {
    // Hide system segments from UI (but keep them in database)
    const isSystemSegment = 
      segment.name === 'all-active-leads' ||
      segment.name === 'never-contact' ||
      segment.name === 'Do Not Contact' ||
      !!segment.never_contact; // Handle SQLite numeric booleans (1/0)

    const matchesFilter = 
      filter === 'all' ||
      (filter === 'active' && segment.is_active) ||
      (filter === 'inactive' && !segment.is_active) ||
      (filter === 'email_ready' && segment.email_list_ready);

    const matchesSearch = 
      segment.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      segment.description?.toLowerCase().includes(searchTerm.toLowerCase());

    return !isSystemSegment && matchesFilter && matchesSearch;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (segment: Segment) => {
    if (segment.never_contact) return 'bg-red-100 text-red-800';
    if (!segment.is_active) return 'bg-gray-100 text-gray-800';
    if (segment.email_list_ready) return 'bg-green-100 text-green-800';
    return 'bg-yellow-100 text-yellow-800';
  };

  const getStatusText = (segment: Segment) => {
    if (segment.never_contact) return 'Do Not Contact';
    if (!segment.is_active) return 'Inactive';
    if (segment.email_list_ready) return 'Email Ready';
    return 'Processing';
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Lead Segments</h1>
          <p className="text-gray-600">Manage and organize leads for targeted email marketing</p>
        </div>
        <button
          onClick={onCreateSegment}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
        >
          <Plus size={20} />
          Create Segment
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap gap-4">
        <div className="flex gap-2">
          {['all', 'active', 'inactive', 'email_ready'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f as any)}
              className={`px-3 py-1 rounded text-sm ${
                filter === f 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Search segments..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="px-3 py-1 border rounded text-sm"
        />
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700">{error}</p>
          <button 
            onClick={() => setError(null)}
            className="text-red-600 underline text-sm"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Segments Grid */}
      <div className="space-y-4">
        {filteredSegments.map(segment => (
          <div key={segment.id} className="bg-white border rounded-lg p-6 hover:shadow-lg transition-shadow">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{segment.name}</h3>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(segment)}`}>
                    {getStatusText(segment)}
                  </span>
                  {segment.auto_tag && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                      Auto
                    </span>
                  )}
                  <span className="text-xs text-gray-500">
                    Priority: {segment.priority}
                  </span>
                </div>
                
                <p className="text-gray-600 mb-4">{segment.description}</p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-gray-500">Leads in Segment</div>
                    <div className="font-semibold text-lg">{segment.active_leads || 0}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Created</div>
                    <div className="font-semibold">{formatDate(segment.created_at)}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Last Evaluated</div>
                    <div className="font-semibold">
                      {segment.last_evaluated ? formatDate(segment.last_evaluated) : 'Never'}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2 ml-4">
                <button
                  onClick={() => onViewSegment(segment)}
                  className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded"
                  title="View leads in segment"
                >
                  <Users size={16} />
                </button>
                
                <button
                  onClick={() => onEditSegment(segment)}
                  className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded"
                  title="Edit segment"
                >
                  <Edit size={16} />
                </button>
                
                <button
                  onClick={() => handleToggleActive(segment.id, segment.is_active)}
                  className={`p-2 rounded ${
                    segment.is_active 
                      ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50' 
                      : 'text-green-600 hover:text-green-700 hover:bg-green-50'
                  }`}
                  title={segment.is_active ? 'Pause segment' : 'Activate segment'}
                >
                  {segment.is_active ? <Pause size={16} /> : <Play size={16} />}
                </button>
                
                <button
                  onClick={() => handleExportSegment(segment.id, segment.name)}
                  className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded"
                  title="Export leads as CSV"
                  disabled={(segment.active_leads || 0) === 0}
                >
                  <Download size={16} />
                </button>
                
                <button
                  onClick={() => handleSyncToMailerLite(segment.id, segment.name)}
                  className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded"
                  title="Sync to MailerLite"
                  disabled={(segment.active_leads || 0) === 0}
                >
                  <Cloud size={16} />
                </button>
                
                <button
                  onClick={() => handleCloneSegment(segment.id, segment.name)}
                  className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded"
                  title="Clone segment"
                >
                  <Copy size={16} />
                </button>
                
                <button
                  onClick={() => handleDeleteSegment(segment.id, segment.name)}
                  className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded"
                  title="Delete segment"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredSegments.length === 0 && (
        <div className="text-center py-12">
          <Target size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No segments found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm ? 'Try adjusting your search criteria' : 'Create your first segment to get started'}
          </p>
          {!searchTerm && (
            <button
              onClick={onCreateSegment}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Create Your First Segment
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SegmentDashboard;